/**
 * The timezones an association can be in (#599), shared by onboarding
 * (`hoa/linear/CreateHoaModal`) and the HOA's Timezone panel.
 *
 * The server accepts any IANA place name; this list is what a person
 * should be choosing between.
 */
export const US_TIMEZONES: { id: string; label: string }[] = [
  { id: 'America/New_York', label: 'Eastern' },
  { id: 'America/Chicago', label: 'Central' },
  { id: 'America/Denver', label: 'Mountain' },
  { id: 'America/Boise', label: 'Mountain (southern Idaho)' },
  { id: 'America/Phoenix', label: 'Mountain, no daylight saving (Arizona)' },
  { id: 'America/Los_Angeles', label: 'Pacific' },
  { id: 'America/Anchorage', label: 'Alaska' },
  { id: 'America/Adak', label: 'Hawaii–Aleutian (Aleutian Islands)' },
  { id: 'Pacific/Honolulu', label: 'Hawaii' },
  { id: 'America/Puerto_Rico', label: 'Atlantic (Puerto Rico)' },
  { id: 'America/St_Thomas', label: 'Atlantic (U.S. Virgin Islands)' },
  { id: 'Pacific/Guam', label: 'Chamorro (Guam)' },
];

/** `America/Chicago` → `Central (America/Chicago)`. */
export const timezoneLabel = (id: string): string => {
  const known = US_TIMEZONES.find((z) => z.id === id);
  return known ? `${known.label} (${id})` : id;
};

/**
 * States that span two zones, and what they span, in plain words.
 *
 * For these the state alone cannot say which zone an association keeps,
 * and for most of them the server's default is the *eastern* zone — which
 * assesses charges early for an association in the western part. So
 * onboarding makes the choice explicit rather than defaulting. Mirrors
 * the split states in `back-end-rs/src/utils/local_date.rs`.
 */
export const SPLIT_ZONE_STATES: Record<string, string> = {
  AK: 'Alaska and Hawaii–Aleutian time',
  FL: 'Eastern and Central time',
  ID: 'Mountain and Pacific time',
  IN: 'Eastern and Central time',
  KS: 'Central and Mountain time',
  KY: 'Eastern and Central time',
  MI: 'Eastern and Central time',
  ND: 'Central and Mountain time',
  NE: 'Central and Mountain time',
  OR: 'Pacific and Mountain time',
  SD: 'Central and Mountain time',
  TN: 'Central and Eastern time',
  TX: 'Central and Mountain time',
};

/** Does this state leave the timezone a question someone has to answer? */
export const needsTimezoneChoice = (state: string | null | undefined): boolean =>
  !!state && state.toUpperCase() in SPLIT_ZONE_STATES;
